import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCurrentVTOSession,
  selectIsVTOProcessing,
  selectVTOResultImageId,
  startVTOSession,
  setVTOResult,
  setVTOError,
  setProcessingStage,
  setProcessingProgress
} from '../../../redux/slices/vtoSlice';
import { selectActiveItems } from '../../../redux/slices/clothingItemsSlice';
import { selectPersonImageDisplayProps } from '../../../redux/selectors/personImageSelectors';
import { setVirtualTryOnResult } from '../../../redux/slices/personImageSlice';
import {
  useGetUserVTOResultsQuery,
  useFavoriteVTOResultMutation,
  useDeleteVTOResultMutation,
  useTogglePublishVTOResultMutation
} from '../../../redux/apis/endpoints/vtoResultApi';
import { useVTOResults } from '../../../hooks/useVTOResults';
import virtualTryOnService from '../../../services/virtualTryOnService';
import { downloadImage, downloadCanvasAsImage } from '../../../utils/imageDownload';

export const useVTOBottomPanel = (droppedItems = []) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', or null

  const currentSession = useSelector(selectCurrentVTOSession);
  const isGenerating = useSelector(selectIsVTOProcessing);
  const activeItems = useSelector(selectActiveItems);
  const personImageProps = useSelector(selectPersonImageDisplayProps);

  // Get background state
  const background = useSelector(state => state.background.background);

  // RTK Query hooks for VTO results
  const {
    data: vtoResultsData,
    isLoading: isLoadingVTOResults,
    error: vtoResultsError,
    refetch: refetchVTOResults
  } = useGetUserVTOResultsQuery({
    limit: 20,
    sort: 'createdAt:desc'
  });

  const { vtoResults: hooksVtoResults } = useVTOResults();

  const [favoriteVTOResult] = useFavoriteVTOResultMutation();
  const [deleteVTOResult] = useDeleteVTOResultMutation();
  const [togglePublishVTOResult] = useTogglePublishVTOResultMutation();

  // Extract VTO results from RTK Query response
  const vtoResults = vtoResultsData?.results || [];

  // Handle virtual try-on generation
  const handleVirtualTryOn = useCallback(async () => {
    if (!personImageProps?.image || activeItems.length === 0) {
      console.warn('Cannot generate VTO: missing person image or clothing items');
      return;
    }

    try {
      // Start VTO session
      dispatch(startVTOSession({
        personImageId: personImageProps.activePersonImageId,
        clothingIds: activeItems.map(item => item.id)
      }));

      dispatch(setProcessingStage('processing'));
      dispatch(setProcessingProgress(25));

      const result = await virtualTryOnService.generateTryOn({
        personImage: personImageProps.image,
        clothingItems: activeItems
      });

      if (result.success && result.imageUrl) {
        dispatch(setProcessingProgress(75));

        // Set VTO result in the session
        dispatch(setVTOResult({
          resultImageId: result.imageUrl, // This would be an ID in real implementation
          metadata: {
            ...result.metadata,
            clothingItems: activeItems,
            generatedAt: Date.now()
          }
        }));

        // Also update person image slice for display
        dispatch(setVirtualTryOnResult({
          metadata: {
            ...result.metadata,
            vtoResultUrl: result.imageUrl,
            clothingItems: activeItems
          }
        }));

        dispatch(setProcessingProgress(100));
      } else {
        throw new Error(result.error || 'Generation failed');
      }
    } catch (error) {
      console.error('VTO generation error:', error);
      dispatch(setVTOError({
        error: error.message,
        stage: 'processing'
      }));
    }
  }, [dispatch, personImageProps, activeItems]);

  // Handle image download - downloads the composite canvas when wearing items
  const handleDownload = useCallback(async (imageUrl, canvasElement = null) => {
    try {
      // If there are active items being worn and canvas element is provided, download the composite canvas
      if (activeItems.length > 0 && canvasElement) {
        console.log('📸 Downloading composite canvas with wearing items:', activeItems.length);
        await downloadCanvasAsImage(canvasElement, `vto-composite-${Date.now()}.jpg`);
      }
      // Otherwise, download the VTO result image URL if available
      else if (imageUrl) {
        console.log('📸 Downloading VTO result image');
        await downloadImage(imageUrl, `vto-result-${Date.now()}.jpg`);
      } else {
        console.warn('⚠️ No image or canvas to download');
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  }, [activeItems]);

  // Handle person image update (fallback - should be overridden by prop)
  const handleUpdatePersonImage = useCallback(() => {
    console.log('Update person image requested - fallback handler');
  }, []);

  // Handle favorite/unfavorite VTO result
  const handleToggleFavorite = useCallback(async (vtoResultId, isFavorite) => {
    try {
      await favoriteVTOResult({ id: vtoResultId, favorite: !isFavorite }).unwrap();
      console.log(`✅ VTO result ${!isFavorite ? 'favorited' : 'unfavorited'} successfully`);
    } catch (error) {
      console.error('❌ Failed to toggle favorite:', error);
    }
  }, [favoriteVTOResult]);

  // Handle delete VTO result
  const handleDeleteVTOResult = useCallback(async (vtoResultId) => {
    try {
      await deleteVTOResult(vtoResultId).unwrap();
      console.log('✅ VTO result deleted successfully');
    } catch (error) {
      console.error('❌ Failed to delete VTO result:', error);
    }
  }, [deleteVTOResult]);

  // Handle toggle share to public
  const handleTogglePublish = useCallback(async (vtoResultId, isPublic) => {
    try {
      await togglePublishVTOResult({ id: vtoResultId, isPublic }).unwrap();
      console.log(`✅ VTO result ${isPublic ? 'published' : 'privatized'} successfully`);
    } catch (error) {
      console.error('❌ Failed to toggle publish status:', error);
    }
  }, [togglePublishVTOResult]);

  // Handle Save Look functionality - captures full canvas state
  const handleSaveLook = useCallback(async () => {
    // Check if we have at least a VTO result OR manually placed items
    const hasVTOResult = currentSession?.resultImageId && personImageProps?.image && activeItems.length > 0;
    const hasManualItems = droppedItems && droppedItems.length > 0;

    if (!hasVTOResult && !hasManualItems) {
      console.warn('Cannot save look: no VTO result or manually placed items');
      return;
    }

    setIsSaving(true);
    setSaveStatus(null);

    try {
      // If there's a VTO result, mark it as favorite
      if (hasVTOResult) {
        const currentVTOResult = vtoResults.find(result =>
          (Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url) === currentSession.resultImageId
        );

        if (currentVTOResult && !currentVTOResult.isFavorite) {
          await favoriteVTOResult({
            id: currentVTOResult.id,
            favorite: true
          }).unwrap();
        }
      }

      // Save complete look to localStorage with full canvas state
      const lookData = {
        id: Date.now(),
        name: `Look ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,

        // Include all manually placed items from canvas
        items: droppedItems || [],

        // Include VTO items if present
        vtoItems: hasVTOResult ? activeItems.map((item, index) => ({
          id: item.id,
          image: item.image,
          name: item.clothesName || item.name,
          x: 50 + (index * 10),
          y: 50 + (index * 10),
          width: 200,
          height: 200,
          rotation: 0,
          zIndex: (droppedItems?.length || 0) + index + 1,
          isVirtualTryOn: true,
          originalImage: item.image,
          tryOnMetadata: {
            generatedAt: Date.now(),
            vtoResultUrl: currentSession.resultImageId,
            personImage: personImageProps.image
          }
        })) : [],

        // Include background state
        background: background || { type: 'color', color: '#FFFFFF' },

        // VTO metadata
        hasVirtualTryOn: hasVTOResult,
        virtualTryOnResults: hasVTOResult ? [{
          imageUrl: currentSession.resultImageId,
          metadata: currentSession.metadata
        }] : [],
        personImage: personImageProps?.image || null,
        vtoResultUrl: hasVTOResult ? currentSession.resultImageId : null,

        // Timestamps
        createdAt: Date.now()
      };

      const existingLooks = JSON.parse(localStorage.getItem('savedLooks') || '[]');
      existingLooks.push(lookData);
      localStorage.setItem('savedLooks', JSON.stringify(existingLooks));

      setSaveStatus('success');
      console.log('✅ Look saved successfully with full canvas state:', {
        manualItems: droppedItems?.length || 0,
        vtoItems: hasVTOResult ? activeItems.length : 0,
        background: background?.type,
        hasVTOResult
      });

      setTimeout(() => setSaveStatus(null), 3000);

    } catch (error) {
      console.error('❌ Failed to save look:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 5000);
    } finally {
      setIsSaving(false);
    }
  }, [currentSession, personImageProps, activeItems, vtoResults, favoriteVTOResult, background, droppedItems]);

  // Create currentGeneration enhanced with real DB properties if available
  const resultId = currentSession.vtoResultId;
  const dbResult = resultId ? hooksVtoResults.find(r => r.id === resultId) : null;

  const currentGeneration = currentSession.resultImageId ? {
    id: resultId,
    isPublic: dbResult?.isPublic || false,
    imageUrl: currentSession.resultImageId,
    metadata: currentSession.metadata
  } : null;

  return {
    // State
    currentGeneration,
    currentSession,
    isGenerating,
    activeItems,
    isSaving,
    saveStatus,

    // Actions
    handleVirtualTryOn,
    handleDownload,
    handleUpdatePersonImage,
    handleSaveLook,
    handleTogglePublish,

    // Computed
    canGenerate: !isGenerating && activeItems.length > 0 && personImageProps?.image,
    canDownload: !isGenerating && currentSession.resultImageId,
    // Can save look if there's a VTO result OR manually placed items
    canSaveLook: !isGenerating && !isSaving && (
      (currentSession.resultImageId && personImageProps?.image && activeItems.length > 0) ||
      (droppedItems && droppedItems.length > 0)
    )
  };
};

export default useVTOBottomPanel;